import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { TypographyH1 } from '@heidi/design-system/typography';
import { cn, isVowel } from '@heidi/utils';
import { countries } from '@heidi/utils/countries';
import { CommandList } from 'cmdk';
import { Check, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { FilterSettings } from '../../contracts';

interface LoggedOutStateTitleProps {
  search: string;
  specialty: string;
  setSpecialty: (specialty: string) => void;
  location: string;
  setLocation: (location: string) => void;
  specialties: { id: string | number; name: string; localizedName: string }[] | undefined;
  total: number | undefined;
  localizedCountries: {
    name: string;
    countryCode?: string | null;
    localizedName: string;
  }[];
  getLocalizedNameFromEnglish: (name: string) => string;
}

const LoggedOutStateTitle = ({
  search,
  specialty,
  setSpecialty,
  location,
  setLocation,
  specialties,
  total,
  localizedCountries,
  getLocalizedNameFromEnglish,
}: LoggedOutStateTitleProps) => {
  const [isSpecialtyPopoverOpen, setIsSpecialtyPopoverOpen] = useState(false);
  const [isLocationPopoverOpen, setIsLocationPopoverOpen] = useState(false);

  // Get the localized name for the selected location
  const selectedLocationLocalizedName = useMemo(() => {
    if (
      location === String(FilterSettings.ALL) ||
      location === String(FilterSettings.UNDEFINED) ||
      location === String(FilterSettings.INITIAL)
    ) {
      return null;
    }
    return getLocalizedNameFromEnglish(location);
  }, [location, getLocalizedNameFromEnglish]);

  return (
    <div>
      <TypographyH1 className={`${!search && 'hidden'}`}>
        <span className="text-text-secondary">{total} Results for</span> &ldquo;
        {search}&rdquo;
      </TypographyH1>
      <div className={`flex w-full flex-wrap justify-center gap-4 ${search && 'hidden'}`}>
        <div className={`flex flex-wrap justify-center gap-4`}>
          <TypographyH1 className="text-text-secondary">
            I am{' '}
            {specialty !== String(FilterSettings.ALL)
              ? isVowel(specialty)
                ? 'an'
                : 'a'
              : ''}
          </TypographyH1>
          <Popover open={isSpecialtyPopoverOpen} onOpenChange={setIsSpecialtyPopoverOpen}>
            <PopoverTrigger asChild>
              <div
                role="combobox"
                aria-controls="specialty-popover"
                aria-expanded={isSpecialtyPopoverOpen}
                className={`flex cursor-pointer items-center gap-1 ${
                  specialty !== String(FilterSettings.ALL) && 'border-b-2 border-dashed'
                }`}
              >
                <TypographyH1
                  className={`text-center ${
                    (specialty === String(FilterSettings.UNDEFINED) ||
                      specialty === String(FilterSettings.INITIAL)) &&
                    'text-text-secondary'
                  }`}
                >
                  {specialty === String(FilterSettings.ALL)
                    ? 'All specialities'
                    : specialty === String(FilterSettings.UNDEFINED) ||
                        specialty === String(FilterSettings.INITIAL)
                      ? 'please select'
                      : (specialties?.find(
                          (s) => s.name.toLowerCase() === specialty.toLowerCase()
                        )?.localizedName ?? specialty)}
                </TypographyH1>
                <ChevronDown size={24} />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-11/12 max-w-sm p-0">
              <Command
                filter={(value, searchTerm) => {
                  if (value.includes(searchTerm)) {
                    return 1;
                  }
                  return 0;
                }}
              >
                <CommandInput placeholder="Please select" />
                <CommandList className="max-h-80 overflow-y-auto">
                  <CommandEmpty>No specialties found.</CommandEmpty>
                  <CommandGroup>
                    {specialties?.map((spec) => (
                      <CommandItem
                        key={spec.id}
                        value={spec.localizedName}
                        onSelect={() => {
                          setSpecialty(spec.name);
                          setIsSpecialtyPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 size-4',
                            spec.name.toLowerCase() === specialty.toLowerCase()
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {spec.localizedName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <TypographyH1 className="text-text-secondary">
            based {location !== String(FilterSettings.ALL) && 'in'}
          </TypographyH1>
          <Popover open={isLocationPopoverOpen} onOpenChange={setIsLocationPopoverOpen}>
            <PopoverTrigger className="h-fit" asChild>
              <div
                role="combobox"
                aria-controls="location-popover"
                aria-expanded={isLocationPopoverOpen}
                className={`flex h-fit cursor-pointer items-center gap-1 ${
                  location !== String(FilterSettings.ALL) && 'border-b-2 border-dashed'
                }`}
              >
                {countries.find((country) => country.name === location)?.countryCode && (
                  <Image
                    src={`/country-flags/3x2/${countries
                      .find((country) => country.name === location)!
                      .countryCode?.toUpperCase()}.svg`}
                    alt={location}
                    width={44}
                    height={(44 * 2) / 3}
                    className="rounded-sm"
                  />
                )}
                <TypographyH1
                  className={`text-center ${
                    (location === String(FilterSettings.UNDEFINED) ||
                      location === String(FilterSettings.INITIAL)) &&
                    'text-text-secondary'
                  }`}
                >
                  {location === String(FilterSettings.ALL)
                    ? 'Worldwide'
                    : location === String(FilterSettings.UNDEFINED) ||
                        location === String(FilterSettings.INITIAL)
                      ? 'please select'
                      : selectedLocationLocalizedName || location}
                </TypographyH1>
                <ChevronDown size={24} />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-11/12 max-w-sm p-0">
              <Command
                filter={(value, searchTerm) => {
                  if (value.includes(searchTerm)) {
                    return 1;
                  }
                  return 0;
                }}
              >
                <CommandInput placeholder="Please select" />
                <CommandList className="max-h-80 overflow-y-auto">
                  <CommandEmpty>No locations found.</CommandEmpty>
                  <CommandGroup>
                    {localizedCountries.map((country) => (
                      <CommandItem
                        key={country.countryCode}
                        value={country.localizedName}
                        onSelect={() => {
                          setLocation(country.name);
                          setIsLocationPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 size-4',
                            country.name === location ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Image
                          src={`/country-flags/3x2/${country.countryCode?.toUpperCase()}.svg`}
                          alt={country.localizedName}
                          width={28}
                          height={(28 * 2) / 3}
                          className="mr-2 rounded-sm"
                        />
                        {country.localizedName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default LoggedOutStateTitle;
